import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Copy, RefreshCw, Trash2, ScrollText, Cpu, Loader2, Wifi, WifiOff, Pencil } from "lucide-react";
import dayjs from "dayjs";

function timeAgo(iso) {
  if (!iso) return "belum pernah";
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s lalu`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m lalu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}j lalu`;
  return `${Math.floor(h / 24)}h lalu`;
}

function DeviceCard({ d, isMaster, onChanged, onView, index }) {
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState(d.name);
  const [renaming, setRenaming] = useState(false);
  const rename = async () => {
    setRenaming(true);
    try {
      await api.patch(`/devices/${d.id}`, { name: editName });
      toast.success("Nama PC diperbarui");
      setEditOpen(false);
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRenaming(false); }
  };
  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(d.api_key || "");
      toast.success("API key disalin");
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = d.api_key || "";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success("API key disalin"); }
      catch (err) { toast.error("Gagal menyalin API key"); }
      document.body.removeChild(ta);
    }
  };
  const regen = async () => {
    try {
      await api.post(`/devices/${d.id}/regenerate-key`);
      toast.success("API key baru dibuat");
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async () => {
    try {
      await api.delete(`/devices/${d.id}`);
      toast.success(`${d.name} dihapus`);
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      data-testid={`device-card-${d.id}`}
      className="bg-[#111827] border border-[#1F2937] rounded-xl p-5 hover:border-[#374151] transition-colors"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-[#0B0F17] border border-[#1F2937] flex items-center justify-center shrink-0">
            <Cpu size={18} className="text-[#60A5FA]" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-semibold text-white truncate flex items-center gap-1.5">
              {d.name}
              {isMaster && (
                <button
                  data-testid={`rename-device-${d.id}`}
                  onClick={() => { setEditName(d.name); setEditOpen(true); }}
                  className="text-[#6B7280] hover:text-[#60A5FA] shrink-0"
                  title="Ubah nama"
                >
                  <Pencil size={13} />
                </button>
              )}
            </h3>
            <p className="text-xs text-[#9CA3AF]">{d.os} · {d.ip || "no ip"}</p>
          </div>
        </div>
        <div
          data-testid={`device-status-badge-${d.id}`}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold shrink-0 ${
            d.online ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#EF4444]/10 text-[#EF4444]"
          }`}
        >
          <span className={`w-2 h-2 rounded-full ${d.online ? "bg-[#10B981] status-dot-online" : "bg-[#EF4444]"}`} />
          {d.online ? "Online" : "Offline"}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-4 text-xs text-[#9CA3AF]">
        <span className="flex items-center gap-1.5">
          {d.online ? <Wifi size={13} className="text-[#10B981]" /> : <WifiOff size={13} className="text-[#EF4444]" />}
          ping {timeAgo(d.last_ping)}
        </span>
        {isMaster && d.assigned_users?.length > 0 && (
          <span className="truncate">👥 {d.assigned_users.join(", ")}</span>
        )}
      </div>

      {isMaster && (
        <div className="mt-4 bg-[#0B0F17] border border-[#1F2937] rounded-lg p-2 flex items-center gap-2">
          <code className="text-[11px] text-[#6B7280] font-mono truncate flex-1">{d.api_key}</code>
          <button data-testid={`copy-apikey-${d.id}`} onClick={copyKey} className="text-[#9CA3AF] hover:text-white p-1"><Copy size={14} /></button>
          <button data-testid={`regen-apikey-${d.id}`} onClick={regen} className="text-[#9CA3AF] hover:text-[#F59E0B] p-1"><RefreshCw size={14} /></button>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <Button
          data-testid={`view-logs-${d.id}`}
          onClick={() => onView(d.id)}
          className="flex-1 h-9 bg-[#1F2937] hover:bg-[#374151] text-white text-sm rounded-lg"
        >
          <ScrollText size={15} className="mr-1.5" /> Lihat Log
        </Button>
        {isMaster && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid={`delete-device-${d.id}`} variant="ghost" size="icon" className="h-9 w-9 text-[#9CA3AF] hover:text-[#EF4444] hover:bg-[#EF4444]/10">
                <Trash2 size={16} />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#111827] border-[#1F2937] text-white">
              <AlertDialogHeader>
                <AlertDialogTitle>Hapus {d.name}?</AlertDialogTitle>
                <AlertDialogDescription className="text-[#9CA3AF]">
                  Semua log perangkat ini akan ikut terhapus. Tindakan ini tidak bisa dibatalkan.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-[#1F2937] border-[#374151] text-white hover:bg-[#374151]">Batal</AlertDialogCancel>
                <AlertDialogAction onClick={del} className="bg-[#EF4444] hover:bg-[#DC2626]">Hapus</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-white">
          <DialogHeader><DialogTitle className="font-display">Ubah Nama PC</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-[#9CA3AF]">Nama PC</Label>
            <Input
              data-testid={`rename-input-${d.id}`}
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && editName.trim()) rename(); }}
              className="bg-[#0B0F17] border-[#1F2937]"
            />
          </div>
          <DialogFooter>
            <Button
              data-testid={`rename-save-${d.id}`}
              onClick={rename}
              disabled={renaming || !editName.trim()}
              className="bg-[#3B82F6] hover:bg-[#2563EB]"
            >
              {renaming ? <Loader2 className="animate-spin" size={16} /> : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

export default function Devices() {
  const { isMaster } = useAuth();
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", os: "", ip: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/devices");
      setDevices(data);
    } catch (e) { toast.error("Gagal memuat perangkat"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const createDevice = async () => {
    setSaving(true);
    try {
      await api.post("/devices", form);
      toast.success("Perangkat ditambahkan");
      setOpen(false);
      setForm({ name: "", os: "", ip: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onlineCount = devices.filter((d) => d.online).length;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Perangkat</h1>
          <p className="text-[#9CA3AF] mt-1 text-sm">
            {devices.length} PC · <span className="text-[#10B981]">{onlineCount} online</span> · <span className="text-[#EF4444]">{devices.length - onlineCount} offline</span>
          </p>
        </div>
        {isMaster && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-device-button" className="bg-[#3B82F6] hover:bg-[#2563EB] rounded-xl h-10">
                <Plus size={18} className="mr-1.5" /> Tambah PC
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#111827] border-[#1F2937] text-white">
              <DialogHeader><DialogTitle className="font-display">Tambah Perangkat</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">Nama PC</Label>
                  <Input data-testid="device-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="PC-LAB-04" className="bg-[#0B0F17] border-[#1F2937]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF]">OS</Label>
                    <Input data-testid="device-os-input" value={form.os} onChange={(e) => setForm({ ...form, os: e.target.value })} placeholder="Windows 11" className="bg-[#0B0F17] border-[#1F2937]" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF]">IP</Label>
                    <Input data-testid="device-ip-input" value={form.ip} onChange={(e) => setForm({ ...form, ip: e.target.value })} placeholder="192.168.1.30" className="bg-[#0B0F17] border-[#1F2937]" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="save-device-button" onClick={createDevice} disabled={saving || !form.name} className="bg-[#3B82F6] hover:bg-[#2563EB]">
                  {saving ? <Loader2 className="animate-spin" size={16} /> : "Simpan"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#3B82F6]" size={28} /></div>
      ) : devices.length === 0 ? (
        <div className="text-center py-20 text-[#9CA3AF]">Belum ada perangkat yang bisa diakses.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {devices.map((d, i) => (
            <DeviceCard key={d.id} d={d} index={i} isMaster={isMaster} onChanged={load} onView={(id) => navigate(`/logs?device=${id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}
